document.addEventListener('DOMContentLoaded', () => {
	const bar = document.querySelector('.announcement-bar');
	if (bar) {
		w4dInitAnnouncementBarMarquee(bar);

		bar.addEventListener(
			'command',
			(e) => {
				if (e.command !== '--dismiss') return;

				bar.setAttribute('aria-hidden', 'true');
				bar.classList.add('is-dismissing');

				bar._w4dStopMarquee?.();

				bar.addEventListener(
					'transitionend',
					() => {
						bar.remove();
					},
					{ once: true }
				);

				const COOKIE_NAME = 'announcement_bar_dismissed';
				window.W4D_FrontendHelper.setCookie(COOKIE_NAME, 'true', 7, 'Lax', false);
			},
			{ capture: true }
		);
	}
});

/**
 * "Now playing"-style auto-scroll for the announcement bar title.
 *
 * Only ever moves the text when it doesn't fit `.announcement-bar__title-viewport`
 * on one line. Detection is done via IntersectionObserver: a 1px sentinel sits
 * right after the title text inside the track, and the viewport (a fixed-width,
 * overflow:hidden box) is used as the observer root. If the sentinel isn't
 * intersecting, the text is wider than the space available.
 */
function w4dInitAnnouncementBarMarquee(bar) {
	const viewport = bar.querySelector('.announcement-bar__title-viewport');
	const track = bar.querySelector('.announcement-bar__title-track');
	const sentinel = bar.querySelector('.announcement-bar__title-sentinel');

	if (!viewport || !track || !sentinel) return;

	const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
	if (reduceMotion.matches) return;

	const READ_DELAY = 1800; // ms to wait before the very first scroll, so the message can be read
	const START_PAUSE = 3000; // ms to hold at the start before each subsequent scroll
	const END_PAUSE = 1800; // ms to hold once fully scrolled to the end
	const REWIND_MS = 600; // ms duration of the rewind-back animation
	const SPEED = 40; // px/second - keeps scroll speed consistent regardless of title length

	let running = false;
	let timeoutId = null;
	let animation = null;
	const controller = new AbortController();

	// Hover, focus and a hidden tab each hold the scroll; every new phase starts in their combined state.
	// Only a running phase is paused: play() on a finished one would rewind and run it again.
	const holds = new Set();
	const applyHolds = () => {
		if (holds.size && animation?.playState === 'running') animation.pause();
		else if (!holds.size && animation?.playState === 'paused') animation.play();
	};
	const setHold = (reason, on) => {
		if (on) holds.add(reason);
		else holds.delete(reason);
		applyHolds();
	};
	const animate = (keyframes, options) => {
		animation = track.animate(keyframes, options);
		applyHolds();
		return animation.finished.catch(() => {});
	};

	const wait = (ms) =>
		new Promise((resolve) => {
			timeoutId = setTimeout(resolve, ms);
		});

	const overflowDistance = () => track.scrollWidth - viewport.clientWidth;

	const observer = new IntersectionObserver(
		([entry]) => {
			if (!entry.isIntersecting && !running) cycle(true);
		},
		{ root: viewport, threshold: 1 }
	);

	async function cycle(isFirstRun) {
		observer.unobserve(sentinel);

		let distance = overflowDistance();
		if (distance <= 0) {
			viewport.classList.remove('is-scrolled');
			observer.observe(sentinel);
			return;
		}

		running = true;

		await wait(isFirstRun ? READ_DELAY : START_PAUSE);
		if (!running) return;

		// Re-measure in case a resize happened during the wait.
		distance = overflowDistance();
		if (distance <= 0) {
			running = false;
			viewport.classList.remove('is-scrolled');
			observer.observe(sentinel);
			return;
		}

		viewport.classList.add('is-scrolled');

		await animate([{ transform: 'translateX(0)' }, { transform: `translateX(-${distance}px)` }], {
			duration: (distance / SPEED) * 1000,
			easing: 'linear',
			fill: 'forwards',
		});
		if (!running) return;

		await wait(END_PAUSE);
		if (!running) return;

		viewport.classList.remove('is-scrolled');

		await animate([{ transform: `translateX(-${distance}px)` }, { transform: 'translateX(0)' }], {
			duration: REWIND_MS,
			easing: 'ease-in-out',
			fill: 'forwards',
		});
		if (!running) return;

		cycle(false);
	}

	// Defer sentinel activation until idle, then await font readiness
	const startObserving = () => {
		if (document.fonts?.ready) {
			document.fonts.ready.then(() => {
				if (!controller.signal.aborted) observer.observe(sentinel);
			});
		} else {
			observer.observe(sentinel);
		}
	};

	if ('requestIdleCallback' in window) {
		requestIdleCallback(startObserving, { timeout: 3000 });
	} else {
		window.addEventListener('load', startObserving, { once: true });
	}

	const link = bar.querySelector('.announcement-bar__link');
	link?.addEventListener('pointerenter', () => setHold('hover', true), { signal: controller.signal });
	link?.addEventListener('pointerleave', () => setHold('hover', false), { signal: controller.signal });
	link?.addEventListener('focusin', () => setHold('focus', true), { signal: controller.signal });
	link?.addEventListener('focusout', (e) => setHold('focus', link.contains(e.relatedTarget)), {
		signal: controller.signal,
	});
	document.addEventListener('visibilitychange', () => setHold('hidden', document.hidden), {
		signal: controller.signal,
	});

	// Already under the pointer, focused or in a hidden tab when this ran
	if (link?.matches(':hover')) holds.add('hover');
	if (link?.matches(':focus-within')) holds.add('focus');
	if (document.hidden) holds.add('hidden');

	bar._w4dStopMarquee = () => {
		running = false;
		clearTimeout(timeoutId);
		animation?.cancel();
		viewport.classList.remove('is-scrolled');
		observer.disconnect();
		controller.abort();
	};
}
